import { Host, h } from "@stencil/core";
import { analyzeLabelExistence, getElementInternalsLabel } from "../../common/analysis/accessibility";
const ARIA_BUSY = "aria-busy";
const ARIA_DESCRIBEDBY = "aria-describedby";
let autoId = 0;
/**
 * The ch-status is an element that provides a loading indicator.
 * It informs the loading state in various parts of the user interface,
 * such as buttons, overlays, and other elements.
 *
 * @status experimental
 */
export class ChStatus {
    constructor() {
        this.accessibleName = undefined;
        this.loadingRegionRef = undefined;
    }
    // Create a unique ID for the control
    #statusId = `ch-status-${autoId++}`;
    internals;
    el;
    regionLoadingRefChanged(_, oldValue) {
        this.#removeAriaBusyAndAriaDescribedByInRegion(oldValue);
    }
    #getControlId = () => this.el.id ?? this.#statusId;
    #setAriaBusyAndAriaDescribedByInRegion = () => {
        if (!this.loadingRegionRef) {
            return;
        }
        this.loadingRegionRef.setAttribute(ARIA_BUSY, "true");
        this.loadingRegionRef.setAttribute(ARIA_DESCRIBEDBY, this.#getControlId());
    };
    #removeAriaBusyAndAriaDescribedByInRegion = (regionLoadingRef) => {
        if (!regionLoadingRef) {
            return;
        }
        regionLoadingRef.removeAttribute(ARIA_BUSY);
        regionLoadingRef.removeAttribute(ARIA_DESCRIBEDBY);
    };
    connectedCallback() {
        // Set the unique ID if it was not already set in the Host
        this.el.id ??= this.#statusId;
        this.el.setAttribute("role", "status");
        this.el.setAttribute("aria-live", "polite");
        const labels = this.internals.labels;
        const accessibleNameFromExternalLabel = getElementInternalsLabel(labels);
        // Report any accessibility issue
        analyzeLabelExistence(this.el, "ch-status", labels, accessibleNameFromExternalLabel, this.accessibleName);
    }
    componentWillRender() {
        // Check if the component should be active based on external conditions
        if (this.loadingRegionRef) {
            this.#setAriaBusyAndAriaDescribedByInRegion();
        }
        else {
            this.#removeAriaBusyAndAriaDescribedByInRegion(this.loadingRegionRef);
        }
    }
    disconnectedCallback() {
        this.#removeAriaBusyAndAriaDescribedByInRegion(this.loadingRegionRef);
    }
    render() {
        return (h(Host, { key: '6bd8c40e69bff96cefc9c703350f481cdbb35c8e', "aria-label": this.accessibleName }, h("slot", { key: 'c7609db614408276b9f725818155bc1e3ee8ccb6' })));
    }
    static get is() { return "ch-status"; }
    static get encapsulation() { return "shadow"; }
    static get formAssociated() { return true; }
    static get originalStyleUrls() {
        return {
            "$": ["status.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["status.css"]
        };
    }
    static get properties() {
        return {
            "accessibleName": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string | undefined",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a short string that authors associate with an element\nto provide users of assistive technologies with a label for the element."
                },
                "attribute": "accessible-name",
                "reflect": false
            },
            "loadingRegionRef": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLElement | undefined",
                    "resolved": "HTMLElement",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "If the control is describing the loading progress of a particular region\nof a page, set this property with the reference of the loading region.\nThis will set the `aria-describedby` and `aria-busy` attributes on the\nloading region to improve accessibility while the control is in rendered.\n\nWhen the control detects that is no longer in rendered (aka it is removed\nfrom the DOM), it will remove the `aria-busy` attribute and update (or\nremove if necessary) the`aria-describedby` attribute.\n\nIf an ID is set prior to the component's first render, the ch-status will use\nthis ID for the `aria-describedby`. Otherwise, the ch-status will compute a\nunique ID for this matter.\n\n**Important**: If you are using Shadow DOM, take into account that the\n`loadingRegionRef` must be in the same Shadow Tree as the ch-status.\nOtherwise, the `aria-describedby` binding won't work, since the control ID\nis not visible for the `loadingRegionRef`."
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "loadingRegionRef",
                "methodName": "regionLoadingRefChanged"
            }];
    }
    static get attachInternalsMemberName() { return "internals"; }
}
